// MinerGob — the galaxite economy loop: find -> approach -> face -> suck (10 galaxite
// per 7-update tick) -> deliver to a processor (dock point, rotate south, hand off via
// kmidGalaxiteDelivery) -> resume. Hide() is the half-deactivate the processor docking
// uses (the spot stays occupied). game/Miner.cpp (all 925 lines).

import { GetSideMask } from '../types.ts';
import { markDeadSpan } from '../sm/stream.ts';
import type { SimContext } from '../context.ts';
import { Gob, kfGobActive, kfGobBeingBuilt, kfGobDrawFlashed } from './gob.ts';
import {
  MobileUnitGob,
  kfMuntAggressivenessBits,
  kfMuntCommandPending,
  kwxInvalid,
} from './mobileunit.ts';
import { knMoveTargetReached, knMoveStuck } from './mobileunit.ts';
import {
  MinerConsts,
  InitMobileUnitConstsClass,
  kfUntcHasFullnessIndicator,
  kwcTileHalf,
  kwcTile,
} from './unitconsts.ts';
import { kgtGalaxMiner, kgtProcessor } from './gobtypes.ts';
import { kutGalaxMiner } from '../player/side.ts';
import { kumProcessor } from '../player/masks.ts';
import { kfPlrComputer } from '../player/side.ts';
import {
  Message,
  newMessage,
  cloneMessage,
  kgidNull,
  knHandled,
  kstReservedGlobal,
  kmidReservedEnter,
  kmidReservedExit,
  kmidReservedUpdate,
  kmidHit,
  kmidMineCommand,
  kmidDeliverCommand,
  kmidAttackCommand,
  kmidGalaxiteDelivery,
  kstGuard,
  kstMinerMoveToProcessor,
  kstMinerRotateForEntry,
  kstMinerFindGalaxite,
  kstMinerFaceGalaxite,
  kstMinerStepAside,
  kstMinerApproachGalaxite,
  kstMinerSuck,
} from '../sm/message.ts';
import { kfAniLoop, kfAniIgnoreFirstAdvance, Animation } from '../anim/animation.ts';
import { TcFromWc, WcFromTc, WcTrunc } from '../math/coords.ts';
import {
  TurnToward16,
  CalcDir16,
  Dir16ToDx,
  Dir16ToDy,
  g_mpDirToDx,
  g_mpDirToDy,
  kdir16S,
} from '../math/dir.ts';
import { ktcMax } from '../terrain/terrainmap.ts';
import { IsTileFree } from './tilesearch.ts';
import { registerGobType } from './factory.ts';
import type { IniReader } from '../data/ini.ts';
import {
  ksfxNothing,
  ksfxGalaxMinerMine,
  ksfxGalaxMinerUnderAttack,
  ksfxGalaxMinerDeliver,
  ksfxcNothing,
  ksfxcVehicleDestroyed,
  ksfxcMajor01Select,
  ksfxcMajor01Move,
} from '../data/soundeffects.ts';

// game/ht.h:7373-7375
export const knMinerGalaxiteMax = 100; // two full patches of Galaxite
export const knGalaxiteValue = 10;
export const kcMinerSuckDelay = 7; // updates between Galaxite decrements

// TS-only (issue #208, simVersion >= 4): updates between delivery retries while a
// loaded miner sits in kstGuard still wanting to deliver (~4s, the idle-fidget scale)
export const kcMinerDeliverRetryDelay = 50;

// game/ht.h kxInvalid
const kxInvalid = -1000;

// game/Miner.cpp:18-49 — vacuum.anir + identity strips + processor prereq + fullness
export function InitMinerClass(ctx: SimContext, pini: IniReader): boolean {
  const c = new MinerConsts().bind(ctx);
  c.gt = kgtGalaxMiner;
  c.ut = kutGalaxMiner;
  c.umPrerequisites = kumProcessor;
  c.wf |= kfUntcHasFullnessIndicator;
  const ident = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];
  c.anFiringStripIndices = ident;
  c.anMovingStripIndices = ident;
  c.anIdleStripIndices = ident;
  // Sound effects (Miner.cpp:38-46)
  c.sfxImpact = ksfxNothing;
  c.sfxMine = ksfxGalaxMinerMine;
  c.sfxUnderAttack = ksfxGalaxMinerUnderAttack;
  c.sfxFire = ksfxNothing;
  c.sfxcDestroyed = ksfxcVehicleDestroyed;
  c.sfxcSelect = ksfxcMajor01Select;
  c.sfxcMove = ksfxcMajor01Move;
  c.sfxcAttack = ksfxcNothing;
  return InitMobileUnitConstsClass(ctx, c, pini);
}

export class MinerGob extends MobileUnitGob {
  m_aniVacuum = new Animation();
  m_nGalaxiteAmount = 0;
  m_cDelay = 0;
  m_gidFavoriteProcessor = kgidNull;
  m_tptGalaxite = { tx: kxInvalid, ty: 0 };
  m_fMinerUnderAttack = false; // gpplrLocal-specific (excluded from MPValidate)
  m_fHidden = false;
  m_fAttemptingToDeliver = false;

  // game/Miner.cpp:58-69
  constructor(ctx: SimContext) {
    super(ctx, ctx.unitConsts[kutGalaxMiner] as MinerConsts);
    this.m_aniVacuum.Init(ctx.animDataFor('vacuum.anir'));
    this.StartAnimation(this.m_aniVacuum, 0, 0, kfAniLoop);
    this.m_wfMunt &= ~kfMuntAggressivenessBits;
  }

  // game/Miner.cpp:73-100 (knVerMinerGobState 2)
  override LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 2) return false;
    this.m_nGalaxiteAmount = pstm.ReadWord(); // int in C++ — ZERO-extends (Miner.cpp:77)
    this.m_cDelay = (pstm.ReadByte() << 24) >> 24;
    this.m_gidFavoriteProcessor = pstm.ReadWord();
    this.m_tptGalaxite.tx = (pstm.ReadWord() << 16) >> 16;
    this.m_tptGalaxite.ty = (pstm.ReadWord() << 16) >> 16;
    this.m_fMinerUnderAttack = pstm.ReadByte() !== 0;
    this.m_fHidden = pstm.ReadByte() !== 0;
    this.m_fAttemptingToDeliver = pstm.ReadByte() !== 0;
    return super.LoadState(pstm);
  }

  override SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(2);
    pstm.WriteWord(this.m_nGalaxiteAmount);
    pstm.WriteByte(this.m_cDelay);
    markDeadSpan(pstm, 1); // C++ ctor never inits it — garbage until the first mining tick (B20)
    pstm.WriteWord(this.m_gidFavoriteProcessor);
    pstm.WriteWord(this.m_tptGalaxite.tx);
    pstm.WriteWord(this.m_tptGalaxite.ty);
    markDeadSpan(pstm, 2); // the ctor inits only .tx (Miner.cpp:64) (B20)
    pstm.WriteByte(this.m_fMinerUnderAttack ? 1 : 0);
    pstm.WriteByte(this.m_fHidden ? 1 : 0);
    pstm.WriteByte(this.m_fAttemptingToDeliver ? 1 : 0);
    return super.SaveState(pstm);
  }

  GetGalaxiteAmount(): number {
    return this.m_nGalaxiteAmount;
  } // ht.h:7295
  SetGalaxiteAmount(n: number): void {
    this.m_nGalaxiteAmount = n;
  }
  SetFavoriteProcessor(gid: number): void {
    this.m_gidFavoriteProcessor = gid;
  }
  IsAttemptingToDeliver(gidProcessor: number): boolean {
    return this.m_fAttemptingToDeliver && this.m_gidFavoriteProcessor === gidProcessor;
  }

  // game/Miner.cpp:106-126 — half-deactivate while docked: the spot stays occupied
  Hide(fHide: boolean): void {
    if (this.m_fHidden === fHide) return;

    this.m_fHidden = fHide;
    if (fHide) {
      this.m_ff = (this.m_ff & ~(kfGobActive | kfGobDrawFlashed)) >>> 0;
      this.ctx.host.CloseUnitMenu(this);
      // Miner.cpp:121 Invalidate() — render-only host seam: damage the miner's
      // last-painted bounds so its pixels erase (it stops drawing while hidden)
      this.ctx.host.GobInvalidate(this);
    } else {
      this.m_ff = (this.m_ff | kfGobActive) >>> 0;
      // Miner.cpp:124 Invalidate() — damage under the reappearing miner; the
      // paint's overflow-marking pass then re-marks it for redraw
      this.ctx.host.GobInvalidate(this);
    }
  }

  // game/Miner.cpp:131-136 — don't deactivate inactive gobs (restore active first)
  override Deactivate(): void {
    if (this.m_fHidden) this.m_ff = (this.m_ff | kfGobActive) >>> 0;
    super.Deactivate();
  }

  // game/Miner.cpp:197-203 — miners only "attack" friendly processors (delivery)
  override IsValidTarget(pgobTarget: Gob): boolean {
    return (
      pgobTarget.GetType() === kgtProcessor &&
      this.m_pplr === pgobTarget.GetOwner() &&
      (pgobTarget.GetFlags() & kfGobActive) !== 0
    );
  }

  // game/Miner.cpp:219-282 — galaxite tile -> MineCommand; friendly processor ->
  // DeliverCommand; everything else routes to the MobileUnitGob handler
  override SetTarget(
    gid: number,
    wx: number = 0,
    wy: number = 0,
    wxCenter: number = 0,
    wyCenter: number = 0,
    tcRadius: number = 0,
    wcMoveDistPerUpdate: number = 0,
  ): void {
    if (gid === kgidNull) {
      const pfogm = this.ctx.fogm!;
      if (pfogm.GetGalaxite(TcFromWc(wx), TcFromWc(wy)) !== 0) {
        const msg = newMessage(kmidMineCommand, 0, this.m_gid);
        msg.MineCommand.gidTarget = gid;
        msg.MineCommand.wptTarget.wx = wx;
        msg.MineCommand.wptTarget.wy = wy;
        this.ctx.cmdq.Enqueue(msg);
        return;
      }

      MobileUnitGob.prototype.SetTarget.call(
        this,
        gid,
        wx,
        wy,
        wxCenter,
        wyCenter,
        tcRadius,
        wcMoveDistPerUpdate,
      );
      return;
    }

    const pgobTarget = this.ctx.gobm.GetGob(gid);
    if (pgobTarget === null) return;

    if (pgobTarget.GetType() !== kgtProcessor || !this.IsAlly(pgobTarget.GetSide())) {
      MobileUnitGob.prototype.SetTarget.call(
        this,
        gid,
        wx,
        wy,
        wxCenter,
        wyCenter,
        tcRadius,
        wcMoveDistPerUpdate,
      );
      return;
    }

    // Play deliver sfx (Miner.cpp:267 — ungated; SetTarget runs on the
    // issuing machine) + flash the target Gob (gated local, :270-271)
    this.ctx.host.PlaySfx(ksfxGalaxMinerDeliver);
    if (this.m_pplr === this.ctx.plrLocal) pgobTarget.Flash();

    const msg = newMessage(kmidDeliverCommand, 0, this.m_gid);
    msg.MineCommand.gidTarget = gid;
    msg.MineCommand.wptTarget.wx = wx;
    msg.MineCommand.wptTarget.wy = wy;
    this.ctx.cmdq.Enqueue(msg);
  }

  // game/Miner.cpp:284-329
  Mine(wx: number, wy: number): void {
    if (wx === kwxInvalid) {
      if (this.m_tptGalaxite.tx === kxInvalid) {
        this.SetState(kstMinerFindGalaxite);
      } else {
        // Go to Galaxite closest to the previous location.
        // SEAM NOTE: for an SP HUMAN miner (fIgnoreFog false) the search reads the FOG
        // MAP — machine-local presentation state steering sim behavior. The C++ does
        // exactly this (Miner.cpp:304); it is deterministic only because SP has a
        // single machine. MP and computer players bypass the fog (true here), so the
        // lockstep never sees the coupling.
        const pfogm = this.ctx.fogm!;
        const found = pfogm.FindNearestGalaxite(
          this.ctx,
          this.m_tptGalaxite.tx,
          this.m_tptGalaxite.ty,
          (this.m_pplr.GetFlags() & kfPlrComputer) !== 0 || this.ctx.fMultiplayer,
        );
        if (found !== null) {
          this.m_tptGalaxite = found;
          this.m_wptTarget.wx = WcFromTc(this.m_tptGalaxite.tx);
          this.m_wptTarget.wy = WcFromTc(this.m_tptGalaxite.ty);
          this.SetState(kstMinerApproachGalaxite);
        } else {
          // Stay put (in the way of other miners, as the C++ notes)
          this.ctx
            .sim!.GetLevel()
            ?.GetTriggerMgr?.()
            ?.SetConditionTrue(
              8 /* knMinerCantFindGalaxiteCondition */,
              GetSideMask(this.GetSide()),
            ); // Miner.cpp direct
          this.SetState(kstGuard);
        }
      }
    } else {
      this.m_wptTarget.wx = wx;
      this.m_wptTarget.wy = wy;
      this.m_tptGalaxite.tx = TcFromWc(this.m_wptTarget.wx);
      this.m_tptGalaxite.ty = TcFromWc(this.m_wptTarget.wy);
      this.SetState(kstMinerApproachGalaxite);
    }
  }

  // game/Miner.cpp:331-347
  override PerformAction(szAction: string): void {
    const head = IniScanfHead(szAction);
    if (head === null) return;
    if (head === 5 /* knMineUnitAction */) {
      const msg = newMessage(kmidMineCommand, 0, this.m_gid);
      msg.MineCommand.gidTarget = kgidNull;
      msg.MineCommand.wptTarget.wx = kwxInvalid;
      msg.MineCommand.wptTarget.wy = kwxInvalid;
      this.ctx.smm!.SendMsg(msg);
      return;
    }
    super.PerformAction(szAction);
  }

  // game/Miner.cpp:879-908
  FindClosestProcessor(): number {
    let tcxyClosest = ktcMax;
    let gidClosest = kgidNull;
    for (
      let pgobT = this.ctx.gobm.GetFirstGob();
      pgobT !== null;
      pgobT = this.ctx.gobm.GetNextGob(pgobT)
    ) {
      if (pgobT.GetType() !== kgtProcessor) continue;
      if (pgobT.GetOwner() !== this.m_pplr) continue;
      if (pgobT.GetFlags() & kfGobBeingBuilt) continue;
      if (!(pgobT.GetFlags() & kfGobActive)) continue;

      const tpt = { tx: 0, ty: 0 };
      pgobT.GetTilePosition(tpt);
      const tc = this.CalcRange(tpt.tx, tpt.ty, this);
      if (tc < tcxyClosest) {
        gidClosest = pgobT.GetId();
        tcxyClosest = tc;
      }
    }
    return gidClosest;
  }

  // game/Miner.cpp:910-923 — direct send (SetTarget does user-feedback things)
  SendDeliverCommand(gidProcessor: number): void {
    const msg = newMessage(kmidDeliverCommand, 0, this.m_gid);
    msg.MineCommand.gidTarget = gidProcessor;
    msg.MineCommand.wptTarget.wx = 0;
    msg.MineCommand.wptTarget.wy = 0;
    this.ctx.smm!.SendMsg(msg);
  }

  // game/Miner.cpp:406-855
  override ProcessStateMachineMessage(st: number, pmsg: Message): number {
    if (st === kstReservedGlobal) {
      switch (pmsg.mid) {
        case kmidHit:
          // A docked (inactive) miner ignores hits entirely
          if (!(this.m_ff & kfGobActive)) return knHandled;

          // under-attack announce: host/local only
          if (this.ctx.plrLocal === this.m_pplr) {
            if (!this.m_fMinerUnderAttack) {
              this.m_fMinerUnderAttack = true;
              // Miner.cpp:428-431 — gsndm.PlaySfx(ksfxGalaxMinerUnderAttack) +
              // ShowAlert(kidsMinerUnderAttack); presentation = host seam
              this.ctx.host.NotifyMinerUnderAttack();
            }
          }
          return super.ProcessStateMachineMessage(st, pmsg);

        case kmidDeliverCommand:
          if (this.InTransition()) {
            this.m_msgPending = cloneMessage(pmsg);
            this.m_wfMunt |= kfMuntCommandPending;
          } else {
            let pgobTarget: Gob | null = null;
            this.m_gidTarget = pmsg.DeliverCommand.gidTarget;
            if (this.m_gidTarget !== kgidNull) pgobTarget = this.ctx.gobm.GetGob(this.m_gidTarget);
            if (pgobTarget === null) {
              this.m_gidTarget = this.FindClosestProcessor();
              if (this.m_gidTarget !== kgidNull)
                pgobTarget = this.ctx.gobm.GetGob(this.m_gidTarget);
            }

            if (pgobTarget !== null && !(pgobTarget.GetFlags() & kfGobBeingBuilt)) {
              this.m_gidFavoriteProcessor = pgobTarget.GetId();

              // Dock point: the center column tile in front of the entrance
              this.m_wptTarget.wx = pgobTarget.m_wx + kwcTile + kwcTileHalf;
              this.m_wptTarget.wy = pgobTarget.m_wy + kwcTile * 2 + kwcTileHalf;
              this.SetState(kstMinerMoveToProcessor);
            } else {
              // sim-quirks D2 (issue #208): no processor exists right now — from v4 keep
              // wanting to deliver so the kstGuard retry finds one built later (the C++
              // parked here forever unless some other delivery completed)
              if (this.ctx.simVersion >= 4) this.m_fAttemptingToDeliver = true;
              this.SetState(kstGuard);
            }
          }
          return knHandled;

        case kmidMineCommand:
          if (this.InTransition()) {
            this.m_msgPending = cloneMessage(pmsg);
            this.m_wfMunt |= kfMuntCommandPending;
          } else {
            this.Mine(pmsg.MineCommand.wptTarget.wx, pmsg.MineCommand.wptTarget.wy);
          }
          return knHandled;

        case kmidAttackCommand:
          // Miners have no attack: swallow the auto-response impulse
          return knHandled;
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    //-----------------------------------------------------------------------
    if (st === kstGuard) {
      // sim-quirks D2 (issue #208, simVersion >= 4): a loaded miner parked in guard that
      // still wants to deliver (knMoveStuck en route, or no processor existed) retries the
      // delivery every kcMinerDeliverRetryDelay updates. The C++ only ever re-woke these
      // from NotifyMinersAttemptingDelivery — a delivery COMPLETING or the processor dying
      // — so a miner that got stuck while its processor idled stayed full forever.
      // m_cDelay is dormant in kstGuard (kstMinerFaceGalaxite/kstMinerSuck re-init it on
      // entry), so it serves as the retry countdown without new savegame state.
      if (
        pmsg.mid === kmidReservedUpdate &&
        this.ctx.simVersion >= 4 &&
        this.m_fAttemptingToDeliver &&
        this.m_nGalaxiteAmount > 0 &&
        (this.m_ff & kfGobActive) !== 0
      ) {
        this.m_cDelay -= this.m_unvl.GetUpdateCount();
        if (this.m_cDelay < 0) {
          this.m_cDelay = kcMinerDeliverRetryDelay;
          this.SendDeliverCommand(this.m_gidFavoriteProcessor);
          return knHandled;
        }
        this.m_unvl.MinSkip(this.m_cDelay); // SP: wake by the retry point at the latest
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    //-----------------------------------------------------------------------
    if (st === kstMinerMoveToProcessor) {
      switch (pmsg.mid) {
        case kmidReservedEnter:
          this.MoveEnter(true);
          return knHandled;
        case kmidReservedExit:
          this.MoveExit();
          return knHandled;
        case kmidReservedUpdate:
          switch (this.MoveUpdate()) {
            case knMoveTargetReached: {
              // Re-verify the target survived the trip with the same owner
              const pgobProcessor = this.ctx.gobm.GetGob(this.m_gidTarget);
              if (pgobProcessor !== null && this.m_pplr === pgobProcessor.GetOwner()) {
                this.SetState(kstMinerRotateForEntry);
              } else {
                const gid = this.FindClosestProcessor();
                if (gid !== kgidNull) {
                  this.SendDeliverCommand(gid);
                } else {
                  // sim-quirks D2 (issue #208): same orphan-parking as the DeliverCommand
                  // arm — from v4 stay flagged so the kstGuard retry recovers
                  if (this.ctx.simVersion >= 4) this.m_fAttemptingToDeliver = true;
                  this.SetState(kstGuard);
                }
              }
              break;
            }
            case knMoveStuck:
              // The processor scans for m_fAttemptingToDeliver miners when free
              this.m_fAttemptingToDeliver = true;
              this.SetState(kstGuard);
              break;
          }
          return knHandled;
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    if (st === kstMinerRotateForEntry) {
      if (pmsg.mid === kmidReservedUpdate) {
        this.m_unvl.MinSkip();

        // Rotate to face south (the dock facing)
        if (this.m_dir !== kdir16S) {
          this.m_dir = TurnToward16(kdir16S, this.m_dir);
          this.StartAnimation(
            this.m_ani,
            this.m_pmuntc.anMovingStripIndices[this.m_dir],
            0,
            kfAniLoop | kfAniIgnoreFirstAdvance,
          );
        } else {
          const pgobProcessor = this.ctx.gobm.GetGob(this.m_gidTarget);
          if (pgobProcessor !== null) {
            this.ctx.smm!.SendMsgFrom(kmidGalaxiteDelivery, this.m_gid, this.m_gidTarget);
          }

          // Sit in guard while the processor extracts; it sends kmidMineCommand
          // (negative target) when done
          this.m_fAttemptingToDeliver = false;
          this.SetState(kstGuard);
          this.m_fMinerUnderAttack = false;
        }
        this.DefUpdate();
        return knHandled;
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    if (st === kstMinerFindGalaxite) {
      if (pmsg.mid === kmidReservedUpdate) {
        if (this.m_nGalaxiteAmount === knMinerGalaxiteMax) {
          this.SendDeliverCommand(this.m_gidFavoriteProcessor);
        } else {
          const tx = TcFromWc(this.m_wx);
          const ty = TcFromWc(this.m_wy);
          // Dir16ToDx/Dy do the dir16/2 mapping (raw dir16 indexed OOB — fixed in master)
          const txTarget = tx + Dir16ToDx(this.m_dir);
          const tyTarget = ty + Dir16ToDy(this.m_dir);

          this.m_wptTarget.wx = WcFromTc(txTarget);
          this.m_wptTarget.wy = WcFromTc(tyTarget);
          const pfogm = this.ctx.fogm!;
          if (pfogm.GetGalaxite(txTarget, tyTarget) !== 0) {
            this.m_tptGalaxite.tx = txTarget;
            this.m_tptGalaxite.ty = tyTarget;
            this.SetState(kstMinerSuck);
          } else {
            // same SP-human fog coupling as Mine() above (Miner.cpp:624)
            const found = pfogm.FindNearestGalaxite(
              this.ctx,
              tx,
              ty,
              (this.m_pplr.GetFlags() & kfPlrComputer) !== 0 || this.ctx.fMultiplayer,
            );
            if (found === null) {
              // Deliver what we have & park; never-found means alert + guard
              if (this.m_tptGalaxite.tx === kwxInvalid) {
                this.ctx
                  .sim!.GetLevel()
                  ?.GetTriggerMgr?.()
                  ?.SetConditionTrue(
                    8 /* knMinerCantFindGalaxiteCondition */,
                    GetSideMask(this.GetSide()),
                  ); // Miner.cpp direct
                this.SetState(kstGuard);
              } else {
                this.SendDeliverCommand(this.m_gidFavoriteProcessor);
              }
            } else {
              this.m_tptGalaxite = found;
              if (tx === this.m_tptGalaxite.tx && ty === this.m_tptGalaxite.ty) {
                // On top of it: step aside to an unoccupied adjacent tile
                for (let dir = 0; dir < 8; dir++) {
                  const txT = tx + g_mpDirToDx[dir];
                  const tyT = ty + g_mpDirToDy[dir];
                  if (!IsTileFree(this.ctx, txT, tyT).free) continue;

                  this.m_wptTarget.wx = WcFromTc(txT);
                  this.m_wptTarget.wy = WcFromTc(tyT);
                  this.SetState(kstMinerStepAside);
                  break;
                }
                this.m_unvl.MinSkip(); // retry next update if no spot
              } else {
                this.m_wptTarget.wx = WcFromTc(this.m_tptGalaxite.tx);
                this.m_wptTarget.wy = WcFromTc(this.m_tptGalaxite.ty);
                this.SetState(kstMinerApproachGalaxite);
              }
            }
          }
        }
        this.DefUpdate();
        return knHandled;
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    if (st === kstMinerFaceGalaxite) {
      switch (pmsg.mid) {
        case kmidReservedEnter:
          this.m_cDelay = 0;
          return knHandled;
        case kmidReservedUpdate: {
          // A little rotation delay
          this.m_cDelay -= this.m_unvl.GetUpdateCount();
          if (this.m_cDelay < 0) {
            this.m_cDelay = 1;

            const dirTo = CalcDir16(
              this.m_tptGalaxite.tx - TcFromWc(this.m_wx),
              this.m_tptGalaxite.ty - TcFromWc(this.m_wy),
            );
            if (this.m_dir !== dirTo) {
              this.m_dir = TurnToward16(dirTo, this.m_dir);
              this.StartAnimation(
                this.m_ani,
                this.m_pmuntc.anMovingStripIndices[this.m_dir],
                0,
                kfAniLoop | kfAniIgnoreFirstAdvance,
              );
            } else {
              this.SetState(kstMinerSuck);
            }
          }
          this.m_unvl.MinSkip(this.m_cDelay);
          this.DefUpdate();
          return knHandled;
        }
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    if (st === kstMinerStepAside) {
      switch (pmsg.mid) {
        case kmidReservedEnter:
          this.MoveEnter(true);
          return knHandled;
        case kmidReservedExit:
          this.MoveExit();
          return knHandled;
        case kmidReservedUpdate:
          switch (this.MoveUpdate()) {
            case knMoveTargetReached:
              this.SetState(kstMinerFaceGalaxite);
              break;
            case knMoveStuck:
              this.SetState(kstGuard);
              break;
          }
          return knHandled;
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    if (st === kstMinerApproachGalaxite) {
      switch (pmsg.mid) {
        case kmidReservedEnter:
          this.MoveEnter(true);
          return knHandled;
        case kmidReservedExit:
          this.MoveExit();
          return knHandled;
        case kmidReservedUpdate:
          // Within 1 tile of the target?
          if (!this.InTransition()) {
            if (
              Math.abs(WcTrunc(this.m_wx) - WcTrunc(this.m_wptTarget.wx)) <= kwcTile &&
              Math.abs(WcTrunc(this.m_wy) - WcTrunc(this.m_wptTarget.wy)) <= kwcTile
            ) {
              this.SetState(kstMinerFaceGalaxite);
              return knHandled;
            }
          }

          switch (this.MoveUpdate()) {
            case knMoveTargetReached:
              this.SetState(kstMinerFaceGalaxite);
              break;
            case knMoveStuck:
              this.SetState(kstGuard);
              break;
          }
          return knHandled;
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    if (st === kstMinerSuck) {
      switch (pmsg.mid) {
        case kmidReservedEnter:
          this.m_cDelay = kcMinerSuckDelay;
          if (this.m_nGalaxiteAmount === knMinerGalaxiteMax) {
            this.SendDeliverCommand(this.m_gidFavoriteProcessor);
          }
          return knHandled;
        case kmidReservedUpdate: {
          if (this.m_nGalaxiteAmount === knMinerGalaxiteMax) {
            this.MarkRedraw();
            this.SendDeliverCommand(this.m_gidFavoriteProcessor);
          } else {
            const pfogm = this.ctx.fogm!;
            if (pfogm.GetGalaxite(this.m_tptGalaxite.tx, this.m_tptGalaxite.ty) === 0) {
              this.MarkRedraw();
              this.SetState(kstMinerFindGalaxite);
            } else {
              this.AdvanceAnimation(this.m_aniVacuum);

              this.m_cDelay -= this.m_unvl.GetUpdateCount();
              if (this.m_cDelay < 0) {
                this.m_cDelay = kcMinerSuckDelay;
                this.m_unvl.MinSkip(this.m_cDelay);
                pfogm.DecGalaxite(this.m_tptGalaxite.tx, this.m_tptGalaxite.ty);
                this.m_nGalaxiteAmount += 10;
                this.MarkRedraw();
              }
            }
          }
          this.DefUpdate();
          return knHandled;
        }
      }
      return super.ProcessStateMachineMessage(st, pmsg);
    }

    return super.ProcessStateMachineMessage(st, pmsg);
  }
}

// PerformAction head parse: "%d" via the shared scanner
import { IniScanf } from '../data/ini.ts';
function IniScanfHead(szAction: string): number | null {
  const r = IniScanf(szAction, '%d');
  return r.count === 1 ? (r.values[0] as number) : null;
}

// self-register (see factory.ts)
registerGobType(kgtGalaxMiner, (ctx) => new MinerGob(ctx));
